import React, { useState, useCallback, useEffect } from 'react';
import { Button, AutoComplete, message, Input, Select } from 'antd';
import debounce from 'lodash.debounce';
import './TimeAndPayTable.css';
import { getBatch, getBatchesListData, getBatchSuggestions, getBranches, saveBatch, updateBatch } from '../../../services/selfOnboarding';
import useAuth from '../../../hooks/useAuth';
import { formatDateToYYYY } from './CellConfigAndHelpers';
import { getDetails } from '../../../services/auth';
import { SearchOutlined } from '@ant-design/icons';
import BatchSelectModal from './BatchSelectModal';

const BatchInfo = ({
  activeTab,
  rows,
  setRows,
  setEmployeeCount,
  setSelectedRowKeys,
  batchId = '',
  setBatchId = () => {},
  batchSuggestions = [],
  setBatchSuggestions = () => {},
  originalBatchId,
  setOriginalBatchId,
  isDirty,
  setIsDirty,
  startPollingStatus,
  stopPollingStatus,
  setIsSentToKronos = () => {},
  isSentToKronos = false,
  anyInvalidRow,
  selectedBranch,
}) => {
  //   const [originalBatchId, setOriginalBatchId] = useState('');
  const { userConfig } = useAuth();
  const [searchValue, setSearchValue] = useState('');
  const [permissions, setPermissions] = useState([]);

  const [modalVisible, setModalVisible] = useState(false);
  const [batchList, setBatchList] = useState([]); // Store batches for modal
  const [branches, setBranches] = useState([]);

  // Helper to get permission object for current tab
  const getZoomPermission = (tab) => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return tab === 'time' ? permissions.find((p) => p.feature === 'Time Entry') : permissions.find((p) => p.feature === 'PayCode Entry');
  };

  const zoomPerm = getZoomPermission(activeTab);
  const canCreate = !!(zoomPerm && zoomPerm?.access && zoomPerm?.create && zoomPerm?.create !== 'None');
  const canEdit = !!(zoomPerm && zoomPerm?.access && zoomPerm?.edit && zoomPerm?.edit !== 'None');
  const canView = !!(zoomPerm && zoomPerm?.access && zoomPerm?.view && zoomPerm?.view !== 'None');

  // Count unique, non-empty employeeIds
  const uniqueEmployeeIds = new Set(
    rows
      .map(r => r.employeeId && r.employeeId.toString().trim())
      .filter(id => !!id)
    );
  const filledRowsCount = uniqueEmployeeIds.size;

  // Debounced fetch suggestions
  const fetchSuggestions = async (value) => {
    if (!value) return setBatchSuggestions([]);
    try {
      getBatchSuggestions({ type: activeTab, q: value })
        .then(({ data }) => {
          setBatchSuggestions(data || []);
        })
        .catch((err) => {
          console.error(err);
        });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBatchList = async () => {
    // Replace with your API call to fetch batch list for modal
    // Example response structure:
    // [{ batchId, branch, createdBy, createdAt, updatedBy, updatedAt, status }]
    try {
      // You may want to filter by activeTab
      const payload = { batchType: activeTab, userId: userConfig?.userid };
      const { data } = await getBatchesListData(payload);
      setBatchList(
        (data || []).map((b) => ({
          batchId: b.batchId,
          branch: b.branch,
          user: b.user,
          createdBy: b.user,
          createdAt: b.createdAt,
          updatedBy: b.user,
          updatedAt: b.updatedAt,
          status: b.isSentToKronos ? 'Submitted' : 'Saved',
        }))
      );
    } catch (err) {
      setBatchList([]);
    }
  };

  const openModal = async () => {
    await fetchBatchList();
    setModalVisible(true);
  };

  const handleModalSelect = (selectedBatchId) => {
    setBatchId(selectedBatchId);
    handleSelect(selectedBatchId);
  };

  const debouncedFetch = useCallback(debounce(fetchSuggestions, 400), [activeTab]);

  const handleSearch = (value) => {
    setBatchId(value);
    setSearchValue(value);
    if (value.length >= 2) {
      debouncedFetch(value);
    } else {
      setBatchSuggestions([]);
    }
  };

  // Helper to format MM-DD-YYYY to MM-DD-YY
  const formatDateToYY = (val) => {
    if (!val) return val;
    const parts = val.split('-');
    if (parts.length !== 3) return val;
    const [mm, dd, yyyy] = parts;
    // If year is already 2 digits, return as is
    if (yyyy.length === 2) return val;
    return `${mm}-${dd}-${yyyy.slice(-2)}`;
  };

  const formatBatchData = (punchList = [], statusMap = {}) => {
    return punchList.map((p, i) => ({
      key: i + 1, // add key
      nameLocked: !!p.name, // lock name if pre-populated
      ...p,
      transactionStatus: statusMap[p.employeeId]?.find((punch) => punch.punchId === p._id)?.status || null,
      transactionError: statusMap[p.employeeId]?.find((punch) => punch.punchId === p._id)?.lastError || '',
      inDate: formatDateToYY(p.inDate),
      outDate: formatDateToYY(p.outDate),
      amount: p.payCode && p.payCode.includes('$')
        ? p.amount?.toString().startsWith('$') ? p.amount : `$${p.amount || ''}`
        : p.amount?.toString().replace(/^\$/, ''),
    }));
  };

  const handleSelect = async (value) => {
    setBatchId(value);
    try {
      getBatch(value, true)
        .then(({ data }) => {
          const statusMap = data.batch?.submitToKronosStatus || {};
          const formattedRows = formatBatchData(data?.punchList || [], statusMap);
          setRows(formattedRows);
          setEmployeeCount(formattedRows?.length || 0);
          setOriginalBatchId(data?.batch?.batchId || '');
          setIsSentToKronos(!!data.batch?.isSentToKronos);

          // --- Start polling if any row is processing ---
          const anyProcessing = Object.values(statusMap).some((statuses) =>
            statuses.some((status) => status.status === 'processing')
          );
          if (anyProcessing) {
            startPollingStatus(value);
          } else {
            stopPollingStatus();
          }
        })
        .catch((err) => {
          console.error(err);
          message.error('Failed to fetch batch details');
        });
    } catch (err) {
      message.error('Failed to fetch batch details');
    }
  };

  const handleSave = async () => {
    if (!batchId) {
      message.error('Please enter a Batch ID');
      return;
    }
    // permission checks: create vs edit
    const isUpdate = batchId === originalBatchId;
    // if (isUpdate && !canEdit) {
    //   message.error('You do not have permission to edit batches for this tab');
    //   return;
    // }
    // if (!isUpdate && !canCreate) {
    //   message.error('You do not have permission to create batches for this tab');
    //   return;
    // }

    // Clean rows → remove slno, key
    // Filter only rows with values (ignore empty rows)
    const punchList = rows
      .filter((r) => Object.entries(r).some(([k, v]) => k !== 'key' && v !== '' && v !== null && v !== undefined))
      .map((row) => {
        const { key, nameLocked, employeeIdInvalid, wbsIoInvalid, ...rest } = row;
        // Omit _id only if saving a new batch
        if (batchId !== originalBatchId) {
          const { _id, ...restWithoutId } = rest;
          return {
            ...restWithoutId,
            inDate: formatDateToYYYY(rest.inDate),
            outDate: formatDateToYYYY(rest.outDate),
            amount: rest.amount?.toString().replace(/^\$/, ''), // Remove $ prefix
            unit: rest.unit, // <-- Store the unit
          };
        }
        // For update, keep _id
        return {
          ...rest,
          inDate: formatDateToYYYY(rest.inDate),
          outDate: formatDateToYYYY(rest.outDate),
          amount: rest.amount?.toString().replace(/^\$/, ''), // Remove $ prefix
        };
      });

    if (punchList.length === 0) {
      message.error('No entries to save');
      return;
    }

    const requiredFieldsTime = ['employeeId', 'name', 'inDate', 'inTime'];
    const requiredFieldsPay = ['employeeId', 'name', 'inDate', 'payCode', 'amount'];
    const requiredFields = activeTab === 'time' ? requiredFieldsTime : requiredFieldsPay;

    // Only validate non-empty rows
    const nonEmptyRows = rows.filter((row) => Object.entries(row).some(([k, v]) => k !== 'key' && v !== '' && v !== null && v !== undefined));

    // Check for missing required fields in any row
    const missingRequired = nonEmptyRows.some((row) => requiredFields.some((field) => !row[field] || row[field].toString().trim() === ''));

    if (missingRequired) {
      if (activeTab === 'time') {
        message.error('For Time Entry, please fill all compulsory fields: Employee ID, Name, In Date, and In Time');
      } else if (activeTab === 'pay') {
        message.error('For Pay Code Edit, please fill all compulsory fields: Employee ID, Name, In Date, Pay Code, and Amount.');
      } else {
        message.error('Please fill all compulsory fields before saving the batch.');
      }
      return;
    }

    const payload = {
      batchId: batchId,
      batchType: activeTab,
      punchList,
      user: userConfig?.userid,
    };

    try {
      let response;
      if (batchId === originalBatchId) {
        response = await updateBatch({ ...payload, batchUpdates: {} });
        message.success('Batch updated successfully!');

        if (response?.data?.punchList && Array.isArray(response.data.punchList)) {
          const returnedPunchList = response.data.punchList;
          setRows((prevRows) =>
            prevRows.map((row, index) => {
              // Use the index to map the _id from the response punchList
              const updatedRow = returnedPunchList[index];
              return updatedRow ? { ...row, _id: updatedRow._id } : row;
            }),
          );
        }
      } else {
        response = await saveBatch(payload);
        // --- Update rows with _id from response ---
        if (response?.data?.punchList && Array.isArray(response.data.punchList)) {
          const returnedPunchList = response.data.punchList;
          setRows((prevRows) =>
            prevRows.map((row, index) => {
              // Use the index to map the _id from the response punchList
              const updatedRow = returnedPunchList[index];
              return updatedRow ? { ...row, _id: updatedRow._id } : row;
            }),
          );
        }
        message.success('Batch saved successfully!');
        setOriginalBatchId(batchId); // mark this as original
      }
      setSelectedRowKeys([]);
      setIsDirty(false);
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || 'Error saving batch');
    }
  };

  useEffect(() => {
    getRolePermissionDetails();
  },[])

  const getRolePermissionDetails = () => {
    getDetails(userConfig.orgId)
      .then((response) => {
        const fetchRole = response.data.find((role) => 
          role?.selectedRole?.toLowerCase().trim() === userConfig.role?.toLowerCase().trim() && 
          role?.selectedRoleType?.toLowerCase().trim() === userConfig.roleName?.selectedRoleType?.toLowerCase().trim()
        );
        setPermissions(fetchRole.permissions.cap.zoomEntry);
      })
      .catch((err) => {
        console.error(err);
      })
  }

  return (
    <div style={{ marginBottom: 16, width: '40%' }}>
      <table className="batch-info-table">
        <thead>
          <tr>
            <th colSpan={2} className="batch-info-header">
              Batch Data
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="batch-info-label">User</td>
            <td className="batch-info-value">{userConfig?.firstName + ' ' + userConfig?.lastName}</td>
          </tr>
          <tr>
            <td className="batch-info-label">Batch ID</td>
            <td className="batch-info-value">
              <div style={{ display: 'flex', gap: 8, height: '100%' }}>
                <AutoComplete
                  value={batchId}
                  options={batchSuggestions.map((s) => ({ value: s }))}
                  onSearch={handleSearch}
                  onSelect={handleSelect}
                  style={{ width: 250 }}
                  notFoundContent={searchValue.length >= 2 && batchSuggestions.length === 0 ? 'No Items found' : null}
                >
                </AutoComplete>
                <Button
                  icon={<SearchOutlined />}
                  onClick={openModal}
                  style={{
                    border: '1px solid grey',
                    background: '#E4E4E4',
                    boxShadow: 'none',
                  }}
                  tabIndex={-1}
                />
                <Button type="primary" onClick={handleSave} disabled={!batchId || anyInvalidRow || !zoomPerm?.access || !selectedBranch}>
                  Save Batch
                </Button>
              </div>
            </td>
          </tr>
          <tr>
            <td className="batch-info-label">Count of Employees</td>
            <td className="batch-info-value">{filledRowsCount}</td>
          </tr>
        </tbody>
      </table>
      <BatchSelectModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        batches={batchList}
        onSelectBatch={handleModalSelect}
        selectedBatchId={batchId}
      />
    </div>
  );
};

export default BatchInfo;
